import { safeJsonParse } from '@/utils';
import { assertProjectSavePayloadWithinLimit } from '@/features/projects/projectPayloadLimits';

const API_BASE = '/api';
const AUTH_TOKEN_KEY = 'yiblock.auth.token';

export type BackendUserRole = 'admin' | 'teacher' | 'user';

export interface BackendUserProfile {
  username: string;
  role: BackendUserRole;
  displayName: string;
  permissions: string[];
  tokenQuota: number;
  projectQuota: number;
  publicationQuota: number;
  projectCount: number;
  publicationCount: number;
  storageBytes: number;
}

export interface BackendLoginResponse {
  token: string;
  user: BackendUserProfile;
}

export interface BackendAiProvider {
  providerId: string;
  name: string;
  endpoint: string;
  models: string[];
  defaultModel: string;
  docsUrl?: string;
}

export interface BackendAdminAiProvider extends BackendAiProvider {
  apiKey: string;
  enabled: boolean;
  hasKey: boolean;
}

export interface BackendAdminAiProviderTestResult {
  content: string;
  model: string;
}

export type BackendSpeechProviderId = 'browser' | 'custom' | 'qwen-realtime';

export interface BackendAiSpeechRecognitionSummary {
  providerId: BackendSpeechProviderId;
  model: string;
  docsUrl: string;
  configured: boolean;
}

export interface BackendAiSpeechSynthesisSummary {
  providerId: BackendSpeechProviderId;
  model: string;
  voice: string;
  docsUrl: string;
  configured: boolean;
}

export interface BackendAiSpeechSummary {
  recognition: BackendAiSpeechRecognitionSummary;
  synthesis: BackendAiSpeechSynthesisSummary;
}

export interface BackendUiConfig {
  showTopbarLogo: boolean;
  showFooter: boolean;
  footerHtml: string;
}

export interface BackendDataStorageConfig {
  dataDir: string;
}

export interface BackendAdminAiSpeechRecognitionConfig {
  providerId: BackendSpeechProviderId;
  endpoint: string;
  model: string;
  apiKey: string;
  docsUrl: string;
}

export interface BackendAdminAiSpeechSynthesisConfig {
  providerId: BackendSpeechProviderId;
  endpoint: string;
  model: string;
  apiKey: string;
  voice: string;
  docsUrl: string;
}

export interface BackendAdminAiSpeechConfig {
  recognition: BackendAdminAiSpeechRecognitionConfig;
  synthesis: BackendAdminAiSpeechSynthesisConfig;
}

export interface BackendProjectVersionHistoryEntry {
  version: string;
  title: string;
  description: string;
  actorUsername: string;
  source: string;
  createdAt: string;
}

export interface BackendProjectDetail {
  id: string;
  ownerUsername: string;
  publicationSourceProjectId: string | null;
  originMarketProjectId: string | null;
  title: string;
  description: string;
  workspaceXml: string;
  version: string;
  thumbnail: string;
  moduleIds: string[];
  extensions: Record<string, unknown>;
  isPublic: boolean;
  ratingAvg: number;
  ratingCount: number;
  downloadCount: number;
  favorited: boolean;
  myRating: number;
  versionHistory: BackendProjectVersionHistoryEntry[];
  createdAt: string;
  updatedAt: string;
}

export interface BackendMarketProjectSummary {
  id: string;
  ownerUsername: string;
  publicationSourceProjectId: string | null;
  originMarketProjectId: string | null;
  title: string;
  description: string;
  version: string;
  thumbnail: string;
  moduleIds: string[];
  controllerModel: string;
  isPublic: boolean;
  updatedAt: string;
  ratingAvg: number;
  ratingCount: number;
  downloadCount: number;
  favorited: boolean;
  myRating: number;
}

export type BackendMarketSort =
  | 'comprehensive'
  | 'updatedAtAsc'
  | 'updatedAtDesc'
  | 'downloadCountAsc'
  | 'downloadCountDesc'
  | 'ratingAsc'
  | 'ratingDesc';

export type BackendProjectSummary = BackendMarketProjectSummary;

export interface BackendMarketRankingProjectEntry {
  id: string;
  title: string;
  ownerUsername: string;
  thumbnail: string;
  ratingAvg: number;
  downloadCount: number;
}

export interface BackendMarketRankingUserEntry {
  username: string;
  projectCount: number;
  downloadCount: number;
  thumbnail: string;
}

export interface BackendMarketRankingsResponse {
  popularProjects: BackendMarketRankingProjectEntry[];
  activeUsers: BackendMarketRankingUserEntry[];
}

export interface BackendMarketListResponse {
  page: number;
  pageSize: number;
  total: number;
  items: BackendMarketProjectSummary[];
}

export type BackendMarketCommentStatus = 'visible' | 'pending' | 'hidden' | 'rejected' | 'deleted';
export type BackendMarketCommentReportReason = 'abuse' | 'advertising' | 'sexual' | 'privacy' | 'other';

export interface BackendMarketComment {
  id: string;
  projectId: string;
  parentId: string | null;
  authorUsername: string;
  body: string;
  status: BackendMarketCommentStatus;
  moderationReason: string | null;
  reportCount: number;
  canEdit: boolean;
  canDelete: boolean;
  createdAt: string;
  updatedAt: string;
  replies: BackendMarketComment[];
}

export interface BackendMarketCommentList {
  page: number;
  pageSize: number;
  total: number;
  items: BackendMarketComment[];
}

export interface BackendMarketCommentSettings {
  minIntervalSeconds: number;
  maxPerMinute: number;
  maxPerDay: number;
  duplicateWindowMinutes: number;
  updatedBy: string | null;
  updatedAt: string;
}

export interface BackendMarketCommentFilterRule {
  id: string;
  pattern: string;
  matchType: 'contains' | 'regex';
  action: 'reject' | 'pending';
  category: string;
  enabled: boolean;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface BackendMarketCommentAuditItem {
  id: string;
  commentId: string | null;
  projectId: string | null;
  actorUsername: string;
  action: string;
  previousStatus: string | null;
  nextStatus: string | null;
  reason: string | null;
  metadata: Record<string, unknown>;
  createdAt: string;
}

export interface BackendAdminStats {
  users: number;
  projects: number;
  publicProjects: number;
  ratings: number;
  favorites: number;
  downloads: number;
}

export interface BackendServiceAiProviderStatus {
  providerId: string;
  name: string;
  endpoint: string;
  models: string[];
  defaultModel: string;
  enabled: boolean;
  apiKeyConfigured: boolean;
}

export interface BackendServiceDatabaseStatus {
  enabled: boolean;
  connected: boolean;
  poolSize: number;
  idleConnections: number;
}

export interface BackendServiceLogFile {
  name: string;
  sizeBytes: number;
  modifiedUnixSeconds: number | null;
}

export interface BackendServiceStatus {
  processId: number;
  host: string;
  port: number;
  memoryRssBytes: number | null;
  configDir: string;
  dataDir: string;
  logDir: string;
  projectRootSource: string;
  configDirSource: string;
  dataDirSource: string;
  hostSource: string;
  portSource: string;
  databaseSource: string;
  showTopbarLogo: boolean;
  showFooter: boolean;
  defaultTokenQuota: number;
  aiProviders: BackendServiceAiProviderStatus[];
  speechRecognitionProvider: string;
  speechRecognitionModel: string;
  speechRecognitionApiKeyConfigured: boolean;
  speechSynthesisProvider: string;
  speechSynthesisModel: string;
  speechSynthesisVoice: string;
  speechSynthesisApiKeyConfigured: boolean;
  database: BackendServiceDatabaseStatus;
  logFiles: BackendServiceLogFile[];
  latestLog: string;
  logReadError: string | null;
}

export interface BackendAiUsageUserStat {
  username: string;
  questionCharsTotal: number;
  questionCount: number;
  promptTokens: number;
  completionTokens: number;
  totalTokens: number;
  tokenQuota: number;
  remainingTokens: number;
  exhausted: boolean;
  updatedAt: string;
}

export interface BackendAiQuotaConfig {
  defaultTokenQuota: number;
}

export interface BackendAiQuotaStatus {
  username: string;
  tokenQuota: number;
  totalTokensUsed: number;
  remainingTokens: number;
  exhausted: boolean;
}

export interface BackendAdminAiConversationEntry {
  id: number;
  providerId: string;
  model: string;
  modelVersion: string;
  status: string;
  promptContent: string;
  responseContent: string;
  errorMessage: string;
  promptTokens: number;
  completionTokens: number;
  totalTokens: number;
  createdAt: string;
}

export interface BackendAdminAiConversationGroup {
  username: string;
  items: BackendAdminAiConversationEntry[];
}

export interface BackendAdminAiConversations {
  groups: BackendAdminAiConversationGroup[];
}

export interface BackendAdminAiStatsOverview {
  totalRequests: number;
  successRequests: number;
  failedRequests: number;
  totalPromptChars: number;
  totalPromptTokens: number;
  totalCompletionTokens: number;
  totalTokens: number;
  avgDurationMs: number;
  lastAggregatedAt: string;
}

export interface BackendAdminAiStats {
  overview: BackendAdminAiStatsOverview;
  users: BackendAiUsageUserStat[];
}

export interface BackendAiChatResponse {
  content: string;
  truncated: boolean;
  notice?: string;
}

export class BackendApiError extends Error {
  status: number;

  constructor(status: number, message: string) {
    super(message);
    this.status = status;
    this.name = 'BackendApiError';
  }
}

function getStoredToken(): string {
  if (typeof window === 'undefined') return '';
  return window.localStorage.getItem(AUTH_TOKEN_KEY) || '';
}

function parseErrorMessage(raw: string, fallback: string): string {
  if (!raw) return fallback;
  const parsed = safeJsonParse<{ error?: string } | null>(raw, null);
  if (parsed?.error && typeof parsed.error === 'string') {
    return parsed.error;
  }
  return raw;
}

async function requestJson<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers ?? {});
  if (!headers.has('Content-Type') && init.body) {
    headers.set('Content-Type', 'application/json');
  }

  const token = getStoredToken();
  if (token && !headers.has('Authorization')) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  const response = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers,
  });

  const text = await response.text();
  if (!response.ok) {
    const fallback = response.status === 413
      ? '请求内容过大，超过服务器允许的大小限制'
      : `HTTP ${response.status}`;
    throw new BackendApiError(response.status, parseErrorMessage(text, fallback));
  }

  if (!text) {
    return {} as T;
  }

  return JSON.parse(text) as T;
}

async function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => {
      reject(reader.error ?? new Error('Failed to read speech payload'));
    };
    reader.onload = () => {
      const result = reader.result;
      if (typeof result !== 'string') {
        reject(new Error('Failed to encode speech payload'));
        return;
      }
      resolve(result.split(',')[1] || '');
    };
    reader.readAsDataURL(blob);
  });
}

export function setAuthToken(token: string | null): void {
  if (typeof window === 'undefined') return;
  if (!token) {
    window.localStorage.removeItem(AUTH_TOKEN_KEY);
    return;
  }
  window.localStorage.setItem(AUTH_TOKEN_KEY, token);
}

export function getAuthToken(): string {
  return getStoredToken();
}

export async function backendLogin(username: string, password: string): Promise<BackendLoginResponse> {
  return requestJson<BackendLoginResponse>('/auth/login', {
    method: 'POST',
    body: JSON.stringify({ username, password }),
  });
}

export async function backendGetMe(): Promise<BackendUserProfile> {
  return requestJson<BackendUserProfile>('/auth/me');
}

export async function backendLogout(): Promise<void> {
  await requestJson<{ ok: boolean }>('/auth/logout', { method: 'POST' });
}

export async function backendGetUsers(): Promise<BackendUserProfile[]> {
  return requestJson<BackendUserProfile[]>('/users');
}

export async function backendCreateUser(payload: {
  username: string;
  password: string;
  role: BackendUserRole;
  displayName: string;
  students?: string[];
  tokenQuota?: number;
  projectQuota?: number;
  publicationQuota?: number;
}): Promise<BackendUserProfile> {
  return requestJson<BackendUserProfile>('/users', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function backendUpdateUser(username: string, payload: {
  password?: string;
  role?: BackendUserRole;
  displayName?: string;
  students?: string[];
  tokenQuota?: number;
  projectQuota?: number;
  publicationQuota?: number;
}): Promise<BackendUserProfile> {
  return requestJson<BackendUserProfile>(`/users/${encodeURIComponent(username)}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
}

export async function backendGetAiProviders(): Promise<BackendAiProvider[]> {
  return requestJson<BackendAiProvider[]>('/ai/providers');
}

export async function backendGetAiSpeechConfig(): Promise<BackendAiSpeechSummary> {
  return requestJson<BackendAiSpeechSummary>('/ai/speech-config');
}

export async function backendGetUiConfig(): Promise<BackendUiConfig> {
  return requestJson<BackendUiConfig>('/ui-config');
}

export async function backendGetAdminAiProviders(): Promise<BackendAdminAiProvider[]> {
  return requestJson<BackendAdminAiProvider[]>('/admin/ai/providers');
}

export async function backendGetAdminAiSpeechConfig(): Promise<BackendAdminAiSpeechConfig> {
  return requestJson<BackendAdminAiSpeechConfig>('/admin/ai/speech-config');
}

export async function backendGetAdminAiQuotaConfig(): Promise<BackendAiQuotaConfig> {
  return requestJson<BackendAiQuotaConfig>('/admin/ai/quota-config');
}

export async function backendUpdateAdminAiQuotaConfig(payload: BackendAiQuotaConfig): Promise<void> {
  await requestJson<{ ok: boolean }>('/admin/ai/quota-config', {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
}

export async function backendGetAdminUiConfig(): Promise<BackendUiConfig> {
  return requestJson<BackendUiConfig>('/admin/ui-config');
}

export async function backendUpdateAdminUiConfig(payload: BackendUiConfig): Promise<BackendUiConfig> {
  return requestJson<BackendUiConfig>('/admin/ui-config', {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
}

export async function backendGetAdminDataStorageConfig(): Promise<BackendDataStorageConfig> {
  return requestJson<BackendDataStorageConfig>('/admin/data-storage-config');
}

export async function backendUpdateAdminDataStorageConfig(
  payload: BackendDataStorageConfig,
): Promise<BackendDataStorageConfig> {
  return requestJson<BackendDataStorageConfig>('/admin/data-storage-config', {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
}

export async function backendGetAdminAiConversations(): Promise<BackendAdminAiConversations> {
  return requestJson<BackendAdminAiConversations>('/admin/ai/conversations');
}

export async function backendUpdateAiProvider(
  providerId: string,
  payload: {
    name: string;
    endpoint: string;
    models: string[];
    defaultModel: string;
    apiKey: string;
    docsUrl: string;
    enabled: boolean;
  },
): Promise<void> {
  await requestJson<{ ok: boolean }>(`/admin/ai/providers/${encodeURIComponent(providerId)}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
}

export async function backendTestAiProvider(
  providerId: string,
  payload: {
    name: string;
    endpoint: string;
    models: string[];
    defaultModel: string;
    apiKey: string;
    docsUrl: string;
    enabled: boolean;
  },
): Promise<BackendAdminAiProviderTestResult> {
  return requestJson<BackendAdminAiProviderTestResult>(`/admin/ai/providers/${encodeURIComponent(providerId)}/test`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function backendUpdateAdminAiSpeechConfig(
  payload: BackendAdminAiSpeechConfig,
): Promise<void> {
  await requestJson<{ ok: boolean }>('/admin/ai/speech-config', {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
}

export async function backendAiChat(payload: {
  providerId: string;
  model: string;
  temperature: number;
  messages: Array<{ role: 'system' | 'user' | 'assistant'; content: string }>;
}): Promise<BackendAiChatResponse> {
  return requestJson<BackendAiChatResponse>('/ai/chat', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function backendRecognizeSpeech(audioBlob: Blob, language?: string): Promise<string> {
  const audioBase64 = await blobToBase64(audioBlob);
  const response = await requestJson<{ text: string }>('/ai/speech/recognize', {
    method: 'POST',
    body: JSON.stringify({
      audioBase64,
      mimeType: audioBlob.type || 'audio/webm',
      language: language || '',
    }),
  });
  return response.text;
}

export async function backendSynthesizeSpeech(text: string, options?: { voice?: string }): Promise<{
  audioBase64: string;
  mimeType: string;
}> {
  return requestJson<{ audioBase64: string; mimeType: string }>('/ai/speech/synthesize', {
    method: 'POST',
    body: JSON.stringify({ text, voice: options?.voice?.trim() || undefined }),
  });
}

export async function backendGetMyAiQuota(): Promise<BackendAiQuotaStatus> {
  return requestJson<BackendAiQuotaStatus>('/ai/quota');
}

export async function backendClearMyAiHistory(): Promise<void> {
  await requestJson<{ ok: boolean }>('/ai/history', {
    method: 'DELETE',
  });
}

export async function backendListMarketProjects(params: {
  q?: string;
  controllers?: string[];
  moduleIds?: string[];
  favoritesOnly?: boolean;
  sort?: BackendMarketSort;
  page?: number;
  pageSize?: number;
}): Promise<BackendMarketListResponse> {
  const search = new URLSearchParams();
  if (params.q) search.set('q', params.q);
  if (params.controllers?.length) search.set('controller', params.controllers.join(','));
  if (params.moduleIds?.length) search.set('moduleId', params.moduleIds.join(','));
  if (params.favoritesOnly) search.set('favoritesOnly', 'true');
  if (params.sort) search.set('sort', params.sort);
  if (params.page) search.set('page', String(params.page));
  if (params.pageSize) search.set('pageSize', String(params.pageSize));
  const query = search.toString();
  return requestJson<BackendMarketListResponse>(`/market/projects${query ? `?${query}` : ''}`);
}

export async function backendGetMarketRankings(params: {
  q?: string;
  controllers?: string[];
  moduleIds?: string[];
} = {}): Promise<BackendMarketRankingsResponse> {
  const search = new URLSearchParams();
  if (params.q) search.set('q', params.q);
  if (params.controllers?.length) search.set('controller', params.controllers.join(','));
  if (params.moduleIds?.length) search.set('moduleId', params.moduleIds.join(','));
  const query = search.toString();
  return requestJson<BackendMarketRankingsResponse>(`/market/rankings${query ? `?${query}` : ''}`);
}

export async function backendCreateProject(payload: {
  title: string;
  description: string;
  workspaceXml: string;
  version?: string;
  thumbnail?: string;
  extensions?: Record<string, unknown>;
  isPublic?: boolean;
  originMarketProjectId?: string;
}): Promise<BackendProjectSummary> {
  assertProjectSavePayloadWithinLimit(payload);
  return requestJson<BackendProjectSummary>('/projects', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function backendUpdateProject(projectId: string, payload: {
  title: string;
  description: string;
  workspaceXml: string;
  version?: string;
  thumbnail?: string;
  extensions?: Record<string, unknown>;
  isPublic?: boolean;
  originMarketProjectId?: string;
}): Promise<BackendProjectSummary> {
  assertProjectSavePayloadWithinLimit(payload);
  return requestJson<BackendProjectSummary>(`/projects/${encodeURIComponent(projectId)}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
}

export async function backendPublishProject(sourceProjectId: string): Promise<BackendProjectDetail> {
  return requestJson<BackendProjectDetail>(`/projects/${encodeURIComponent(sourceProjectId)}/publish`, {
    method: 'POST',
  });
}

export async function backendUnpublishProject(sourceProjectId: string): Promise<BackendProjectDetail> {
  return requestJson<BackendProjectDetail>(`/projects/${encodeURIComponent(sourceProjectId)}/unpublish`, {
    method: 'POST',
  });
}

export async function backendDeleteProject(
  projectId: string,
  options: { deleteMarketplace?: boolean } = {},
): Promise<void> {
  await requestJson<{ ok: boolean }>(`/projects/${encodeURIComponent(projectId)}`, {
    method: 'DELETE',
    body: JSON.stringify({ deleteMarketplace: Boolean(options.deleteMarketplace) }),
  });
}

export async function backendListMyProjects(): Promise<BackendProjectSummary[]> {
  return requestJson<BackendProjectSummary[]>('/projects/my');
}

export async function backendListRecycledProjects(): Promise<BackendProjectSummary[]> {
  return requestJson<BackendProjectSummary[]>('/projects/recycle-bin');
}

export async function backendRestoreProject(projectId: string): Promise<void> {
  await requestJson<{ ok: boolean }>(`/projects/${encodeURIComponent(projectId)}/restore`, {
    method: 'POST',
  });
}

export async function backendPermanentlyDeleteProject(projectId: string): Promise<void> {
  await requestJson<{ ok: boolean }>(`/projects/${encodeURIComponent(projectId)}/permanent`, {
    method: 'DELETE',
  });
}

export async function backendGetProject(projectId: string): Promise<BackendProjectDetail> {
  return requestJson<BackendProjectDetail>(`/projects/${encodeURIComponent(projectId)}`);
}

export interface BackendCloudVariable {
  name: string;
  value: number;
  revision: number;
  updatedBy: string;
  updatedAt: string;
}

export async function backendListCloudVariables(
  projectId: string,
  sinceRevision = 0,
): Promise<{ revision: number; variables: BackendCloudVariable[] }> {
  return requestJson(`/projects/${encodeURIComponent(projectId)}/cloud-variables?sinceRevision=${Math.max(0, sinceRevision)}`);
}

export async function backendWriteCloudVariable(
  projectId: string,
  name: string,
  value: number,
): Promise<BackendCloudVariable> {
  return requestJson(`/projects/${encodeURIComponent(projectId)}/cloud-variables`, {
    method: 'PUT',
    body: JSON.stringify({ name, value }),
  });
}

export async function backendGetMarketProject(projectId: string): Promise<BackendProjectDetail> {
  return requestJson<BackendProjectDetail>(`/market/projects/${encodeURIComponent(projectId)}`);
}

export async function backendDownloadProject(
  projectId: string,
  options?: { createCopy?: boolean },
): Promise<BackendProjectDetail> {
  const query = options?.createCopy ? '?createCopy=true' : '';
  return requestJson<BackendProjectDetail>(`/market/projects/${encodeURIComponent(projectId)}/download${query}`, {
    method: 'POST',
  });
}

export async function backendFavoriteProject(projectId: string, favorite: boolean): Promise<void> {
  await requestJson<{ ok: boolean }>(`/market/projects/${encodeURIComponent(projectId)}/favorite`, {
    method: 'POST',
    body: JSON.stringify({ favorite }),
  });
}

export async function backendRateProject(projectId: string, rating: number): Promise<void> {
  await requestJson<{ ok: boolean }>(`/market/projects/${encodeURIComponent(projectId)}/rate`, {
    method: 'POST',
    body: JSON.stringify({ rating }),
  });
}

export async function backendListMarketComments(projectId: string, page = 1, pageSize = 10): Promise<BackendMarketCommentList> {
  return requestJson(`/market/projects/${encodeURIComponent(projectId)}/comments?page=${page}&pageSize=${pageSize}`);
}

export async function backendCreateMarketComment(projectId: string, body: string, parentId?: string): Promise<BackendMarketComment> {
  return requestJson(`/market/projects/${encodeURIComponent(projectId)}/comments`, {
    method: 'POST',
    body: JSON.stringify({ body, parentId }),
  });
}

export async function backendUpdateMarketComment(commentId: string, body: string): Promise<BackendMarketComment> {
  return requestJson(`/market/comments/${encodeURIComponent(commentId)}`, {
    method: 'PUT',
    body: JSON.stringify({ body }),
  });
}

export async function backendDeleteMarketComment(commentId: string): Promise<void> {
  await requestJson(`/market/comments/${encodeURIComponent(commentId)}`, { method: 'DELETE' });
}

export async function backendReportMarketComment(
  commentId: string,
  reason: BackendMarketCommentReportReason,
  details = '',
): Promise<void> {
  await requestJson(`/market/comments/${encodeURIComponent(commentId)}/report`, {
    method: 'POST',
    body: JSON.stringify({ reason, details }),
  });
}

export async function backendAdminListMarketComments(params: {
  status?: string;
  projectId?: string;
  username?: string;
  from?: string;
  to?: string;
  minReportCount?: number;
  page?: number;
  pageSize?: number;
} = {}): Promise<BackendMarketCommentList> {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== '') query.set(key, String(value));
  });
  return requestJson(`/admin/market/comments?${query.toString()}`);
}

export async function backendAdminModerateMarketComment(
  commentId: string,
  status: Exclude<BackendMarketCommentStatus, 'deleted'>,
  reason = '',
): Promise<void> {
  await requestJson(`/admin/market/comments/${encodeURIComponent(commentId)}/moderate`, {
    method: 'PUT',
    body: JSON.stringify({ status, reason }),
  });
}

export async function backendAdminBanMarketCommentUser(username: string, reason: string, expiresAt?: string): Promise<void> {
  await requestJson(`/admin/market/comment-bans/${encodeURIComponent(username)}`, {
    method: 'PUT',
    body: JSON.stringify({ reason, expiresAt }),
  });
}

export async function backendAdminUnbanMarketCommentUser(username: string): Promise<void> {
  await requestJson(`/admin/market/comment-bans/${encodeURIComponent(username)}`, { method: 'DELETE' });
}

export async function backendAdminGetMarketCommentSettings(): Promise<BackendMarketCommentSettings> {
  return requestJson('/admin/market/comment-settings');
}

export async function backendAdminUpdateMarketCommentSettings(
  settings: Omit<BackendMarketCommentSettings, 'updatedBy' | 'updatedAt'>,
): Promise<BackendMarketCommentSettings> {
  return requestJson('/admin/market/comment-settings', { method: 'PUT', body: JSON.stringify(settings) });
}

export async function backendAdminListMarketCommentRules(): Promise<BackendMarketCommentFilterRule[]> {
  return requestJson('/admin/market/comment-rules');
}

export async function backendAdminSaveMarketCommentRule(
  rule: Omit<BackendMarketCommentFilterRule, 'id' | 'createdBy' | 'createdAt' | 'updatedAt'>,
  id?: string,
): Promise<BackendMarketCommentFilterRule> {
  return requestJson(id ? `/admin/market/comment-rules/${encodeURIComponent(id)}` : '/admin/market/comment-rules', {
    method: id ? 'PUT' : 'POST',
    body: JSON.stringify(rule),
  });
}

export async function backendAdminDeleteMarketCommentRule(id: string): Promise<void> {
  await requestJson(`/admin/market/comment-rules/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

export async function backendAdminListMarketCommentAudit(page = 1, pageSize = 50): Promise<BackendMarketCommentAuditItem[]> {
  return requestJson(`/admin/market/comment-audit?page=${page}&pageSize=${pageSize}`);
}

export async function backendGetAdminStats(): Promise<BackendAdminStats> {
  return requestJson<BackendAdminStats>('/admin/stats');
}

export async function backendGetAdminServiceStatus(): Promise<BackendServiceStatus> {
  return requestJson<BackendServiceStatus>('/admin/service-status');
}

export async function backendGetAdminAiStats(): Promise<BackendAdminAiStats> {
  return requestJson<BackendAdminAiStats>('/admin/ai/stats');
}
